import React, { FC, SyntheticEvent, ChangeEvent, RefAttributes, forwardRef } from 'react';
import { Entry } from '../Entry';
import { Error } from '../Error';
import { time } from 'uniqid';
import { isEmpty } from 'lodash';
import { Tooltip } from '../Tooltip';

export interface TextInputProps {
  children?: React.ReactNode;
  id?: string;
  name: string;
  value: string;
  onChange: (e: ChangeEvent<HTMLInputElement>) => void;
  onBlur?: (e: SyntheticEvent) => void;
  onFocus?: (e: SyntheticEvent) => void;
  onPaste?: (e: SyntheticEvent) => void;
  className?: string;
  mask?: string | Array<RegExp>;
  maskChar?: string;
  type: 'tel' | 'text' | 'number';
  disabled?: boolean;
  placeholder?: string;
  error?: string;
  showError?: boolean;
  showLabel?: boolean;
  maxLength?: number;
  minLength?: number;
  tooltipContent?: string;
  required?: boolean;
  ariaDescribedby?: string;
}

export const TextInput: FC<TextInputProps & RefAttributes<HTMLInputElement>> = forwardRef<
  HTMLInputElement,
  TextInputProps
>(
  (
    {
      children,
      id = time('input-id-'),
      disabled = false,
      onChange,
      onBlur = () => true,
      onFocus = () => true,
      onPaste = () => true,
      name,
      type,
      className = '',
      mask,
      maskChar,
      value,
      placeholder,
      error,
      showError = true,
      showLabel = true,
      maxLength,
      minLength,
      tooltipContent,
      required,
      ariaDescribedby
    },
    ref
  ) => {
    const ariaDescribedBy = ariaDescribedby ? ariaDescribedby : `${id}-described`;
    return (
      <div className={`d-flex flex-column ${className}`}>
        <div className="d-flex flex-row flex-wrap">
          <label htmlFor={id} className={!showLabel ? 'sr-only' : 'mr-1'}>
            {children}
          </label>
          {!isEmpty(tooltipContent) && (
            <Tooltip
              id={`${name}-tooltip-${id}`}
              ariaLabel={`${name}-tooltip`}
              bubbleWidth={200}
              title={tooltipContent}
              hover={false}
              hasCloseButton={true}
              closeButtonAria={`close-${name}-tooltip`}
            />
          )}
        </div>
        <Entry
          id={id}
          name={name}
          onChange={onChange}
          onBlur={onBlur}
          onPaste={onPaste}
          onFocus={onFocus}
          disabled={disabled}
          type={type}
          ref={ref}
          mask={mask}
          maskChar={maskChar}
          value={value}
          placeholder={placeholder}
          className={`${!isEmpty(error) && !disabled ? 'error' : 'input-field'}`}
          data-testid={id}
          maxLength={maxLength}
          minLength={minLength}
          aria-required={required}
          aria-invalid={!isEmpty(error)}
          aria-describedby={ariaDescribedBy}
        />
        <Error errors={error} showError={showError} callout={false} id={ariaDescribedBy} />
      </div>
    );
  }
);

TextInput.displayName = 'TextInput';
export default TextInput;
